import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button, Table } from 'reactstrap';
import { TextFormat, Translate, getSortState } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort, faSortDown, faSortUp } from '@fortawesome/free-solid-svg-icons';
import { APP_DATE_FORMAT } from 'app/config/constants';
import { ASC, DESC } from 'app/shared/util/pagination.constants';
import { overrideSortStateWithQueryParams } from 'app/shared/util/entity-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities } from './contract-signature.reducer';

export const ContractSignature = () => {
  const dispatch = useAppDispatch();

  const pageLocation = useLocation();
  const navigate = useNavigate();

  const [sortState, setSortState] = useState(
    overrideSortStateWithQueryParams(getSortState(pageLocation, 'id'), pageLocation.search),
  );

  const contractSignatureList = useAppSelector(state => state.contractSignature.entities);
  const loading = useAppSelector(state => state.contractSignature.loading);

  const getAllEntities = () => {
    dispatch(
      getEntities({
        sort: `${sortState.sort},${sortState.order}`,
      }),
    );
  };

  const sortEntities = () => {
    getAllEntities();
    const endURL = `?sort=${sortState.sort},${sortState.order}`;
    if (pageLocation.search !== endURL) {
      navigate(`${pageLocation.pathname}${endURL}`);
    }
  };

  useEffect(() => {
    sortEntities();
  }, [sortState.order, sortState.sort]);

  const sort = p => () => {
    setSortState({
      ...sortState,
      order: sortState.order === ASC ? DESC : ASC,
      sort: p,
    });
  };

  const handleSyncList = () => {
    sortEntities();
  };

  const getSortIconByFieldName = (fieldName: string) => {
    const sortFieldName = sortState.sort;
    const order = sortState.order;
    if (sortFieldName !== fieldName) {
      return faSort;
    }
    return order === ASC ? faSortUp : faSortDown;
  };

  return (
    <div>
      <h2 id="contract-signature-heading" data-cy="ContractSignatureHeading">
        <Translate contentKey="crawlerBackendApp.contractSignature.home.title">Contract Signatures</Translate>
        <div className="d-flex justify-content-end">
          <Button className="me-2" color="info" onClick={handleSyncList} disabled={loading}>
            <FontAwesomeIcon icon="sync" spin={loading} />{' '}
            <Translate contentKey="crawlerBackendApp.contractSignature.home.refreshListLabel">Refresh List</Translate>
          </Button>
          <Link
            to="/contract-signature/new"
            className="btn btn-primary jh-create-entity"
            id="jh-create-entity"
            data-cy="entityCreateButton"
          >
            <FontAwesomeIcon icon="plus" />
            &nbsp;
            <Translate contentKey="crawlerBackendApp.contractSignature.home.createLabel">
              Create new Contract Signature
            </Translate>
          </Link>
        </div>
      </h2>
      <div className="table-responsive">
        {contractSignatureList && contractSignatureList.length > 0 ? (
          <Table responsive>
            <thead>
              <tr>
                <th className="hand" onClick={sort('id')}>
                  <Translate contentKey="crawlerBackendApp.contractSignature.id">ID</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('id')} />
                </th>
                <th className="hand" onClick={sort('status')}>
                  <Translate contentKey="crawlerBackendApp.contractSignature.status">Status</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('status')} />
                </th>
                <th className="hand" onClick={sort('signerName')}>
                  <Translate contentKey="crawlerBackendApp.contractSignature.signerName">Signer Name</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('signerName')} />
                </th>
                <th className="hand" onClick={sort('signerEmail')}>
                  <Translate contentKey="crawlerBackendApp.contractSignature.signerEmail">Signer Email</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('signerEmail')} />
                </th>
                <th className="hand" onClick={sort('ipAddress')}>
                  <Translate contentKey="crawlerBackendApp.contractSignature.ipAddress">Ip Address</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('ipAddress')} />
                </th>
                <th className="hand" onClick={sort('signatureValue')}>
                  <Translate contentKey="crawlerBackendApp.contractSignature.signatureValue">Signature Value</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('signatureValue')} />
                </th>
                <th className="hand" onClick={sort('signedTimestamp')}>
                  <Translate contentKey="crawlerBackendApp.contractSignature.signedTimestamp">
                    Signed Timestamp
                  </Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('signedTimestamp')} />
                </th>
                <th className="hand" onClick={sort('createdTimestamp')}>
                  <Translate contentKey="crawlerBackendApp.contractSignature.createdTimestamp">
                    Created Timestamp
                  </Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('createdTimestamp')} />
                </th>
                <th className="hand" onClick={sort('rejectionReason')}>
                  <Translate contentKey="crawlerBackendApp.contractSignature.rejectionReason">
                    Rejection Reason
                  </Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('rejectionReason')} />
                </th>
                <th>
                  <Translate contentKey="crawlerBackendApp.contractSignature.signingSession">Signing Session</Translate>{' '}
                  <FontAwesomeIcon icon="sort" />
                </th>
                <th />
              </tr>
            </thead>
            <tbody>
              {contractSignatureList.map((contractSignature, i) => (
                <tr key={`entity-${i}`} data-cy="entityTable">
                  <td>
                    <Button tag={Link} to={`/contract-signature/${contractSignature.id}`} color="link" size="sm">
                      {contractSignature.id}
                    </Button>
                  </td>
                  <td>
                    <Translate contentKey={`crawlerBackendApp.SignatureStatus.${contractSignature.status}`} />
                  </td>
                  <td>{contractSignature.signerName}</td>
                  <td>{contractSignature.signerEmail}</td>
                  <td>{contractSignature.ipAddress}</td>
                  <td>{contractSignature.signatureValue}</td>
                  <td>
                    {contractSignature.signedTimestamp ? (
                      <TextFormat type="date" value={contractSignature.signedTimestamp} format={APP_DATE_FORMAT} />
                    ) : null}
                  </td>
                  <td>
                    {contractSignature.createdTimestamp ? (
                      <TextFormat type="date" value={contractSignature.createdTimestamp} format={APP_DATE_FORMAT} />
                    ) : null}
                  </td>
                  <td>{contractSignature.rejectionReason}</td>
                  <td>
                    {contractSignature.signingSession ? (
                      <Link to={`/signing-session/${contractSignature.signingSession.id}`}>
                        {contractSignature.signingSession.id}
                      </Link>
                    ) : (
                      ''
                    )}
                  </td>
                  <td className="text-end">
                    <div className="btn-group flex-btn-group-container">
                      <Button
                        tag={Link}
                        to={`/contract-signature/${contractSignature.id}`}
                        color="info"
                        size="sm"
                        data-cy="entityDetailsButton"
                      >
                        <FontAwesomeIcon icon="eye" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.view">View</Translate>
                        </span>
                      </Button>
                      <Button
                        tag={Link}
                        to={`/contract-signature/${contractSignature.id}/edit`}
                        color="primary"
                        size="sm"
                        data-cy="entityEditButton"
                      >
                        <FontAwesomeIcon icon="pencil-alt" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.edit">Edit</Translate>
                        </span>
                      </Button>
                      <Button
                        onClick={() => (window.location.href = `/contract-signature/${contractSignature.id}/delete`)}
                        color="danger"
                        size="sm"
                        data-cy="entityDeleteButton"
                      >
                        <FontAwesomeIcon icon="trash" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.delete">Delete</Translate>
                        </span>
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          !loading && (
            <div className="alert alert-warning">
              <Translate contentKey="crawlerBackendApp.contractSignature.home.notFound">
                No Contract Signatures found
              </Translate>
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default ContractSignature;
